export type BusinessLanguage = "en" | "es";

/** Country code → smart defaults for currency, timezone, and language shown
 * in the onboarding form. The user can override any field after the default
 * is applied. Spanish-speaking countries default to "es"; everything else
 * (and unknown countries) defaults to "en". */
export const COUNTRY_DEFAULTS: Record<
  string,
  { currency: string; timezone: string; language: BusinessLanguage }
> = {
  MX: { currency: "MXN", timezone: "America/Mexico_City", language: "es" },
  US: { currency: "USD", timezone: "America/New_York", language: "en" },
  CO: { currency: "COP", timezone: "America/Bogota", language: "es" },
  AR: { currency: "ARS", timezone: "America/Argentina/Buenos_Aires", language: "es" },
  CL: { currency: "CLP", timezone: "America/Santiago", language: "es" },
  ES: { currency: "EUR", timezone: "Europe/Madrid", language: "es" },
  PE: { currency: "PEN", timezone: "America/Lima", language: "es" },
};

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function countryDefaults(country: string): {
  currency: string;
  timezone: string;
  language: BusinessLanguage;
} {
  return (
    COUNTRY_DEFAULTS[country] ?? {
      currency: "USD",
      timezone: browserTimezone(),
      language: "en",
    }
  );
}

export const LANGUAGE_OPTIONS: Array<{ code: BusinessLanguage; labelKey: string }> = [
  { code: "en", labelKey: "settings.languageEnglish" },
  { code: "es", labelKey: "settings.languageSpanish" },
];

export const CURRENCY_OPTIONS: Array<{ code: string; label: string }> = [
  { code: "MXN", label: "Mexican Peso (MXN)" },
  { code: "USD", label: "US Dollar (USD)" },
  { code: "EUR", label: "Euro (EUR)" },
  { code: "COP", label: "Colombian Peso (COP)" },
  { code: "ARS", label: "Argentine Peso (ARS)" },
  { code: "CLP", label: "Chilean Peso (CLP)" },
  { code: "PEN", label: "Peruvian Sol (PEN)" },
  { code: "GBP", label: "British Pound (GBP)" },
  { code: "BRL", label: "Brazilian Real (BRL)" },
  { code: "CAD", label: "Canadian Dollar (CAD)" },
];

/** Curated short list shown by default in the timezone select.
 * "Other..." swaps to the full IANA list via `allTimezones()`. */
export const CURATED_TIMEZONES: string[] = [
  "America/Mexico_City",
  "America/Monterrey",
  "America/Tijuana",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/Sao_Paulo",
  "America/Caracas",
  "Europe/Madrid",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

export function allTimezones(): string[] {
  type IntlWithSupportedValues = typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const intl = Intl as IntlWithSupportedValues;
  if (typeof intl.supportedValuesOf === "function") {
    try {
      return intl.supportedValuesOf("timeZone");
    } catch {
      return CURATED_TIMEZONES;
    }
  }
  return CURATED_TIMEZONES;
}
